(() => {
    'use strict';

    if (window.NorkayaPriceSliderV3Initialized) return;
    window.NorkayaPriceSliderV3Initialized = true;

    const FACET = '.price-facet';
    const SLIDER = '.price-slider';
    const RANGE_MIN = '[data-role="range-min"]';
    const RANGE_MAX = '[data-role="range-max"]';
    const RANGE_FILL = '[data-role="range"]';

    const states = new WeakMap();
    let bootFrame = null;

    function toNumber(value) {
        if (value == null || value === '') return 0;

        const normalized = String(value)
            .replace(/\s/g, '')
            .replace(/,/g, '.')
            .replace(/[^\d.]/g, '');

        const num = Number(normalized);

        return Number.isFinite(num) ? Math.round(num) : 0;
    }

    function clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
    }

    function getParts(root) {
        if (!root || !root.isConnected) return null;

        const minText = root.querySelector('input[ref="minInput"]');
        const maxText = root.querySelector('input[ref="maxInput"]');
        const slider = root.querySelector(SLIDER);

        if (!minText || !maxText || !slider) return null;

        const rangeMin = slider.querySelector(RANGE_MIN);
        const rangeMax = slider.querySelector(RANGE_MAX);
        const fill = slider.querySelector(RANGE_FILL);
        const track = slider.querySelector('.price-slider__track');

        if (!rangeMin || !rangeMax || !fill || !track) return null;

        const maxLimit = Math.max(
            1,
            toNumber(slider.dataset.rangeMax || rangeMax.max || maxText.dataset.max || 0)
        );

        return {
            root,
            minText,
            maxText,
            slider,
            rangeMin,
            rangeMax,
            fill,
            track,
            maxLimit,
        };
    }

    function getStartValues(parts) {
        const { slider, minText, maxText, maxLimit } = parts;

        const startMin = clamp(
            toNumber(slider.dataset.startMin || minText.value || 0),
            0,
            maxLimit
        );

        const startMax = clamp(
            toNumber(slider.dataset.startMax || maxText.value || maxLimit),
            0,
            maxLimit
        );

        return {
            min: Math.min(startMin, startMax),
            max: Math.max(startMin, startMax),
        };
    }
    function paint(parts, minValue, maxValue) {
        const { slider, fill, track, maxLimit } = parts;

        const left = (minValue / maxLimit) * 100;
        const right = (maxValue / maxLimit) * 100;

        fill.style.left = `${left}%`;
        fill.style.width = `${Math.max(0, right - left)}%`;

        slider.style.setProperty('--thumb-min-position', `${left}%`);
        slider.style.setProperty('--thumb-max-position', `${right}%`);

        const styles = window.getComputedStyle(slider);
        const thumbSizeRaw = styles.getPropertyValue('--price-thumb-size').trim();
        const thumbSize = Number.parseFloat(thumbSizeRaw) || 12;

        const trackWidth = track.getBoundingClientRect().width;

        if (!trackWidth || trackWidth <= thumbSize * 2) {
            slider.style.setProperty('--thumb-min-left', '0px');
            slider.style.setProperty(
                '--thumb-max-left',
                `calc(100% - ${thumbSize}px)`
            );

            return;
        }

        const maxLeft = trackWidth - thumbSize;

        const minPercent = minValue / maxLimit;
        const maxPercent = maxValue / maxLimit;

        let minThumbLeft = minPercent * maxLeft;
        let maxThumbLeft = maxPercent * maxLeft;

        if (maxThumbLeft - minThumbLeft < thumbSize) {
            const valuePoint = ((minPercent + maxPercent) / 2) * maxLeft;

            minThumbLeft = valuePoint - thumbSize / 2;
            maxThumbLeft = minThumbLeft + thumbSize;
        }

        if (minThumbLeft < 0) {
            minThumbLeft = 0;
            maxThumbLeft = thumbSize;
        }

        if (maxThumbLeft > maxLeft) {
            maxThumbLeft = maxLeft;
            minThumbLeft = maxLeft - thumbSize;
        }

        minThumbLeft = clamp(minThumbLeft, 0, maxLeft);
        maxThumbLeft = clamp(maxThumbLeft, 0, maxLeft);

        slider.style.setProperty('--thumb-min-left', `${minThumbLeft}px`);
        slider.style.setProperty('--thumb-max-left', `${maxThumbLeft}px`);
    }

    function syncUI(parts, minValue, maxValue, source = 'slider') {
        const { minText, maxText, rangeMin, rangeMax, maxLimit } = parts;

        minValue = clamp(Math.round(minValue), 0, maxLimit);
        maxValue = clamp(Math.round(maxValue), 0, maxLimit);

        if (minValue > maxValue) {
            minValue = maxValue;
        }

        rangeMin.value = String(minValue);
        rangeMax.value = String(maxValue);

        if (source !== 'text') {
            minText.value = minValue > 0 ? String(minValue) : '';
            maxText.value = maxValue < maxLimit ? String(maxValue) : '';
        }

        paint(parts, minValue, maxValue);
    }

    function refresh(root) {
        const parts = getParts(root);
        if (!parts) return;

        const values = getStartValues(parts);
        syncUI(parts, values.min, values.max);
    }

    function initFacet(root) {
        const parts = getParts(root);
        if (!parts) return;

        const oldState = states.get(root);

        if (oldState && oldState.slider === parts.slider) {
            oldState.refresh();
            return;
        }

        if (oldState) {
            oldState.destroy();
            states.delete(root);
        }

        const controller = new AbortController();
        const { signal } = controller;

        let activePointerRange = null;
        let activePointerId = null;
        let isFiltering = false;
        let filteringTimeout = null;
        let resultsObserver = null;

        function currentParts() {
            return getParts(root);
        }

        function setFilteringState(state) {
            const parts = currentParts();
            isFiltering = state;

            if (state) {
                activePointerRange = null;
                activePointerId = null;
            }

            if (parts) {
                parts.root.classList.toggle('is-filtering', state);
                parts.slider.classList.toggle('is-filtering', state);
                parts.root.setAttribute('aria-busy', state ? 'true' : 'false');
            }

            if (!state && filteringTimeout) {
                clearTimeout(filteringTimeout);
                filteringTimeout = null;
            }
        }

        function unlockAfterResultsUpdate() {
            const results =
                document.querySelector('#ResultsList') ||
                document.querySelector('[data-testid="product-grid"]') ||
                document.querySelector('.product-grid') ||
                document.querySelector('product-grid-component');

            if (resultsObserver) {
                resultsObserver.disconnect();
                resultsObserver = null;
            }

            if (results) {
                resultsObserver = new MutationObserver(() => {
                    if (resultsObserver) {
                        resultsObserver.disconnect();
                        resultsObserver = null;
                    }

                    window.requestAnimationFrame(() => {
                        setFilteringState(false);

                        window.requestAnimationFrame(() => {
                            scheduleBoot();
                        });
                    });
                });

                resultsObserver.observe(results, {
                    childList: true,
                    subtree: true,
                    attributes: true,
                });
            }

            filteringTimeout = window.setTimeout(() => {
                if (resultsObserver) {
                    resultsObserver.disconnect();
                    resultsObserver = null;
                }

                setFilteringState(false);

                window.requestAnimationFrame(() => {
                    scheduleBoot();
                });
            }, 1200);
        }

        function applyFacet() {
            const parts = currentParts();
            if (!parts || isFiltering) return;

            setFilteringState(true);
            unlockAfterResultsUpdate();

            parts.root.dispatchEvent(new Event('change', { bubbles: true }));
        }

        function setActiveThumb(activeRange) {
            const parts = currentParts();
            if (!parts) return;

            parts.rangeMin.classList.toggle('is-active', activeRange === parts.rangeMin);
            parts.rangeMax.classList.toggle('is-active', activeRange === parts.rangeMax);
        }

        function getTrackRect() {
            const parts = currentParts();
            if (!parts) return null;

            return parts.track.getBoundingClientRect();
        }

        function getValueFromPointer(event) {
            const parts = currentParts();
            const rect = getTrackRect();

            if (!parts || !rect) return 0;

            const width = rect.width || 1;
            const x = clamp(event.clientX - rect.left, 0, width);

            return Math.round((x / width) * parts.maxLimit);
        }

        function getClosestRangeFromPointer(event) {
            const parts = currentParts();
            const rect = getTrackRect();

            if (!parts || !rect) return null;

            const width = rect.width || 1;
            const pointerX = clamp(event.clientX - rect.left, 0, width);

            const minValue = toNumber(parts.rangeMin.value);
            const maxValue = toNumber(parts.rangeMax.value);

            const minX = (minValue / parts.maxLimit) * width;
            const maxX = (maxValue / parts.maxLimit) * width;

            if (minValue === maxValue) {
                const valueX = (minValue / parts.maxLimit) * width;
                return pointerX <= valueX ? parts.rangeMin : parts.rangeMax;
            }

            return Math.abs(pointerX - minX) <= Math.abs(pointerX - maxX)
                ? parts.rangeMin
                : parts.rangeMax;
        }

        function updateRangeFromPointer(activeRange, event) {
            const parts = currentParts();
            if (!parts || !activeRange) return;

            const value = getValueFromPointer(event);

            let minValue = toNumber(parts.rangeMin.value);
            let maxValue = toNumber(parts.rangeMax.value);

            if (activeRange === parts.rangeMin) {
                minValue = clamp(value, 0, maxValue);
            } else {
                maxValue = clamp(value, minValue, parts.maxLimit);
            }

            syncUI(parts, minValue, maxValue, 'slider');
        }

        function getMinValue() {
            const parts = currentParts();
            if (!parts) return 0;

            return clamp(toNumber(parts.minText.value || 0), 0, parts.maxLimit);
        }

        function getMaxValue() {
            const parts = currentParts();
            if (!parts) return 0;

            const raw = parts.maxText.value === '' ? parts.maxLimit : toNumber(parts.maxText.value);

            return clamp(raw, 0, parts.maxLimit);
        }

        function onPointerDown(event) {
            const parts = currentParts();
            if (!parts || isFiltering) return;

            event.preventDefault();
            event.stopPropagation();

            activePointerRange = getClosestRangeFromPointer(event);
            if (!activePointerRange) return;

            activePointerId = event.pointerId;

            setActiveThumb(activePointerRange);
            updateRangeFromPointer(activePointerRange, event);

            parts.slider.setPointerCapture?.(event.pointerId);
        }

        function onPointerMove(event) {
            if (isFiltering || !activePointerRange) return;
            if (activePointerId !== event.pointerId) return;

            event.preventDefault();
            event.stopPropagation();

            updateRangeFromPointer(activePointerRange, event);
        }

        function onPointerUp(event) {
            const parts = currentParts();

            if (!parts || isFiltering || !activePointerRange) return;
            if (activePointerId !== event.pointerId) return;

            event.preventDefault();
            event.stopPropagation();

            updateRangeFromPointer(activePointerRange, event);

            const slider = parts.slider;

            activePointerRange = null;
            activePointerId = null;

            slider.releasePointerCapture?.(event.pointerId);

            syncUI(
                parts,
                toNumber(parts.rangeMin.value),
                toNumber(parts.rangeMax.value),
                'slider'
            );

            applyFacet();
        }

        function onPointerCancel(event) {
            const parts = currentParts();

            activePointerRange = null;
            activePointerId = null;

            parts?.slider.releasePointerCapture?.(event.pointerId);
        }

        function onRangeInput(event) {
            const parts = currentParts();
            if (!parts || isFiltering) return;

            const input = event.target;
            if (!(input instanceof HTMLInputElement)) return;

            const activeRange = input === parts.rangeMin ? parts.rangeMin : parts.rangeMax;

            setActiveThumb(activeRange);

            let minValue = toNumber(parts.rangeMin.value);
            let maxValue = toNumber(parts.rangeMax.value);

            if (minValue > maxValue) {
                if (activeRange === parts.rangeMin) {
                    minValue = maxValue;
                } else {
                    maxValue = minValue;
                }
            }

            syncUI(parts, minValue, maxValue, 'slider');
        }

        function onRangeChange() {
            if (isFiltering) return;
            applyFacet();
        }

        function onTextChange(event) {
            const parts = currentParts();
            if (!parts || isFiltering) return;

            const input = event.target;
            if (!(input instanceof HTMLInputElement)) return;

            let minValue = getMinValue();
            let maxValue = getMaxValue();

            if (minValue > maxValue) {
                if (input === parts.minText) {
                    minValue = maxValue;
                } else {
                    maxValue = minValue;
                }
            }

            syncUI(parts, minValue, maxValue, 'text');
            applyFacet();
        }

        parts.slider.addEventListener('pointerdown', onPointerDown, { signal, capture: true });
        parts.slider.addEventListener('pointermove', onPointerMove, { signal, capture: true });
        parts.slider.addEventListener('pointerup', onPointerUp, { signal, capture: true });
        parts.slider.addEventListener('pointercancel', onPointerCancel, { signal, capture: true });

        parts.rangeMin.addEventListener('focus', () => setActiveThumb(parts.rangeMin), { signal });
        parts.rangeMax.addEventListener('focus', () => setActiveThumb(parts.rangeMax), { signal });

        parts.rangeMin.addEventListener('input', onRangeInput, { signal });
        parts.rangeMax.addEventListener('input', onRangeInput, { signal });
        parts.rangeMin.addEventListener('change', onRangeChange, { signal });
        parts.rangeMax.addEventListener('change', onRangeChange, { signal });

        parts.minText.addEventListener('change', onTextChange, { signal });
        parts.maxText.addEventListener('change', onTextChange, { signal });

        const values = getStartValues(parts);
        syncUI(parts, values.min, values.max);

        states.set(root, {
            slider: parts.slider,
            refresh: () => refresh(root),
            destroy: () => {
                controller.abort();

                if (resultsObserver) {
                    resultsObserver.disconnect();
                    resultsObserver = null;
                }

                if (filteringTimeout) {
                    clearTimeout(filteringTimeout);
                    filteringTimeout = null;
                }
            },
        });
    }

    function boot(context = document) {
        context.querySelectorAll(FACET).forEach(initFacet);
    }

    function scheduleBoot(context = document) {
        if (bootFrame) {
            window.cancelAnimationFrame(bootFrame);
        }

        bootFrame = window.requestAnimationFrame(() => {
            boot(context);

            window.requestAnimationFrame(() => {
                boot(context);
                bootFrame = null;
            });
        });
    }

    document.addEventListener('DOMContentLoaded', () => {
        scheduleBoot();
    });

    document.addEventListener('shopify:section:load', (event) => {
        scheduleBoot(event.target);
    });

    document.addEventListener('filter:update', () => {
        /**
         * filter:update срабатывает до завершения morph.
         * Если сейчас активен price filter, не делаем boot сразу,
         * иначе slider пересчитается по старым data-start-* и отскочит назад.
         */
        if (document.querySelector(`${FACET}.is-filtering`)) return;

        scheduleBoot();
    });

    document.addEventListener(
        'toggle',
        (event) => {
            const details = event.target;

            if (!(details instanceof HTMLDetailsElement)) return;
            if (!details.open) return;

            window.setTimeout(() => scheduleBoot(details), 50);
        },
        true
    );

    document.addEventListener(
        'toggle',
        (event) => {
            const dialog = event.target;

            if (!(dialog instanceof HTMLDialogElement)) return;
            if (!dialog.open) return;
            if (!dialog.closest('dialog-component#filters-drawer')) return;

            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    scheduleBoot(dialog);
                });
            });
        },
        true
    );

    const observer = new MutationObserver((mutations) => {
        let shouldBoot = false;

        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                shouldBoot = [...mutation.addedNodes].some((node) => {
                    if (!(node instanceof HTMLElement)) return false;

                    return node.matches?.(FACET) || node.querySelector?.(FACET);
                });
            }

            if (mutation.type === 'attributes') {
                const target = mutation.target;

                if (target instanceof HTMLElement) {
                    shouldBoot =
                        target.matches?.(SLIDER) ||
                        target.closest?.(SLIDER) ||
                        target.matches?.('.price-facet__input') ||
                        target.matches?.(`${RANGE_MIN}, ${RANGE_MAX}`);
                }
            }

            if (shouldBoot) break;
        }

        if (shouldBoot) {
            scheduleBoot();
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value', 'data-start-min', 'data-start-max', 'data-range-max'],
    });

    scheduleBoot();
})();